<template>
  <form-wrap
    class="content-form"
    @submit="handleSubmit"
  >
    <form-field-set>
      <div
        v-if="contentModalIsLinkMode"
        class="url-input"
      >
        <text-input
          v-model="sourceUrl"
          :error="fieldError(requestType, 'sourceUrl')"
          :label="$t('c.contentForm.form.label.sourceURL')"
          name="sourceUrl"
          direction="vertical"
        />
        <button-input
          class="url-input__reload-button"
          size="square"
          @click="handleFetchUrlData"
        >
          <font-awesome-icon icon="sync-alt" />
        </button-input>
      </div>

      <div v-show="urlIsFetched || contentModalIsManualMode">
        <input
          v-model="sourceUrl"
          type="hidden"
          name="sourceUrl"
        >

        <button
          v-if="false"
          @click="setCustomData"
        >
          Set Custom Data
        </button>
        <text-input
          v-model="title"
          :error="fieldError(requestType, 'title')"
          :label="$t('c.contentForm.form.label.title')"
          :required="true"
          name="title"
          direction="vertical"
        />

        <editor-input
          v-if="shouldShowBody"
          v-model="body"
          :error="fieldError(requestType, 'body')"
          name="body"
          direction="vertical"
        />

        <tags-input
          v-model="tags"
          :label="$t('c.contentForm.form.label.tags')"
          :label-visible="true"
          :options="tagsOptions"
          :required="true"
          name="tags"
          direction="vertical"
        />

        <tags-input
          v-model="countries"
          :label="countriesLabel"
          :label-visible="true"
          :options="countriesOptions"
          name="countries"
          direction="vertical"
        />

        <div class="container">
          <div class="row">
            <div class="col-xl-6">
              <text-input
                v-model="author"
                :error="fieldError(requestType, 'author')"
                :label="$t('c.contentForm.form.label.author')"
                name="author"
                direction="vertical"
              />
            </div>

            <div class="col-xl-6">
              <text-input
                v-model="sourceName"
                :error="fieldError(requestType, 'sourceName')"
                :label="$t('c.contentForm.form.label.source')"
                name="sourceName"
                direction="vertical"
              />
            </div>
          </div>
        </div>

        <text-input
          v-model="publishedAt"
          :error="fieldError(requestType, 'publishedAt')"
          :label="$t('c.contentForm.form.label.publishedAt')"
          :placeholder="$t('c.contentForm.form.placeholder.publishedAt')"
          :required="true"
          name="publishedAt"
          direction="vertical"
          type="date"
        />

        <documents-input
          :remove-method="removeAttachment"
          :multiple="true"
          :value="contentAttachments"
          @input="setAttachments"
        />

        <div class="container">
          <div class="row">
            <div class="col-xl-6">
              <grouped-select-input
                v-model="folderId"
                :error="fieldError(requestType, 'folder')"
                :label="$t('c.contentForm.form.label.selectFolder')"
                :options="groupedFoldersOptions"
                :required="true"
                name="folderId"
                direction="vertical"
                empty-label="Folder auswählen"
              />
            </div>
            <div class="col-xl-6">
              <select-input
                v-model="priority"
                :label="$t('c.contentForm.form.label.priority')"
                :options="priorityOptions"
                :empty-label="null"
                name="priority"
                direction="vertical"
              />
            </div>
          </div>
        </div>

        <div class="container">
          <div class="row">
            <div class="col-xl-6">
              <switch-input
                v-model="shareable"
                :error="fieldError('update', 'shareable')"
                :label="$t('p.clientSettingsPage.form.label.shareable')"
                :hint="$t('p.clientSettingsPage.form.label.shareable_hint')"
                name="shareable"
                direction="vertical"
              />
            </div>
            <div class="col-xl-6">
              <switch-input
                v-if="hasSourceUrl"
                v-model="external"
                :error="fieldError('update', 'external')"
                :label="$t('p.clientSettingsPage.form.label.external')"
                :hint="$t('p.clientSettingsPage.form.label.external_hint')"
                name="external"
                direction="vertical"
              />
            </div>
          </div>
        </div>
      </div>
    </form-field-set>
  </form-wrap>
</template>

<script>
import uuid from 'uuid/v4'
import isBlank from 'is-blank'
import FontAwesomeIcon from '@fortawesome/vue-fontawesome'
import { mapActions, mapGetters, mapState } from 'vuex'
import FormMixin from '@/mixins/forms/Form'
import NotificationsMixin from '@/mixins/notifications/notifications'

export default {
  name: 'ContentForm',
  mixins: [
    FormMixin,
    NotificationsMixin
  ],
  components: {
    FontAwesomeIcon
  },
  props: {
    shouldShowBody: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    ...mapGetters({
      fieldError: 'contentForm/fieldError',
      groupedFoldersOptions: 'groupedFoldersOptions',
      priorityOptions: 'contentForm/priorityOptions',
      countriesOptions: 'countriesOptions',
      tagsOptions: 'tagsOptions'
    }),
    ...mapGetters([
      'contentModalIsLinkMode',
      'contentModalIsManualMode',
    ]),
    ...mapState({
      currentUser: state => state.currentUser.currentUser,
      contentAttachments: state => state.contentAttachments.form.attachments,
      urlIsFetched: state => state.contentForm.urlIsFetched
    }),
    countriesLabel () {
      return this.currentUser.client.workspace == 'db'
        ? this.$t('c.contentForm.form.label.countries_db')
        : this.$t('c.contentForm.form.label.countries')
    },
    requestType () {
      return isBlank(this.id) ? 'create' : 'update'
    },
    id: {
      get () { return this.$store.state.contentForm.form.id },
      set (value) { this.$store.dispatch('contentForm/setId', value) }
    },
    sourceUrl: {
      get () { return this.$store.state.contentForm.form.sourceUrl },
      set (value) { this.$store.dispatch('contentForm/setSourceUrl', value) }
    },
    hasSourceUrl () {
      return !isBlank(this.sourceUrl)
    },
    title: {
      get () { return this.$store.state.contentForm.form.title },
      set (value) { this.$store.dispatch('contentForm/setTitle', value) }
    },
    body: {
      get () { return this.$store.state.contentForm.form.body },
      set (value) { this.$store.dispatch('contentForm/setBody', value) }
    },
    tags: {
      get () { return this.$store.state.contentForm.form.tags },
      set (value) { this.$store.dispatch('contentForm/setTags', value) }
    },
    countries: {
      get () { return this.$store.state.contentForm.form.countries },
      set (value) { this.$store.dispatch('contentForm/setCountries', value) }
    },
    author: {
      get () { return this.$store.state.contentForm.form.author },
      set (value) { this.$store.dispatch('contentForm/setAuthor', value) }
    },
    sourceName: {
      get () { return this.$store.state.contentForm.form.sourceName },
      set (value) { this.$store.dispatch('contentForm/setSourceName', value) }
    },
    publishedAt: {
      get () { return this.$store.state.contentForm.form.publishedAt },
      set (value) { this.$store.dispatch('contentForm/setPublishedAt', value) }
    },
    folderId: {
      get () { return this.$store.state.contentForm.form.folderId },
      set (value) { this.$store.dispatch('contentForm/setFolderId', value) }
    },
    priority: {
      get () { return this.$store.state.contentForm.form.priority },
      set (value) { this.$store.dispatch('contentForm/setPriority', value) }
    },
    shareable: {
      get () { return this.$store.state.contentForm.form.shareable },
      set (value) { this.$store.dispatch('contentForm/setShareable', value) }
    },
    external: {
      get () { return this.$store.state.contentForm.form.external },
      set (value) { this.$store.dispatch('contentForm/setExternal', value) }
    }
  },
  methods: {
    ...mapActions({
      fetchUrlData: 'contentForm/fetchUrlData',
      setAttachments: 'contentAttachments/set',
      removeAttachment: 'contentAttachments/remove',
      reset: 'contentForm/reset',
      show: 'contentForm/show',
      customSubmit: 'contentForm/customSubmit',
      closeContentModal: 'closeContentModal'
    }),
    handleFetchUrlData () {
      this.fetchUrlData()
        .catch(this.somethingWentWrongNotification)
    },
    handleSubmit () {
      this.customSubmit()
        .then(this.closeContentModal)
        .then(this.savedNotification)
        .then(() => {
          const content = this.$store.state.content.content

          if(content.external && !isBlank(content.sourceUrl)) {
            this.fetchContentsForFolder(content.folderId)
              .then((response) => {
                this.setContents(response)
                this.$router.push({ name: 'FoldersDetail', params: {
                  folderId: content.folderId
                }})
              })
          } else {
            this.$router.go({ name: 'ContentPage', params: {
              contentId: content.id
            }})
          }
        })
    }
  }
}
</script>

<style lang="sass" scoped>
.url-input
  position: relative

  .form-section
    padding-right: 81px

  .url-input__reload-button
    bottom: 0
    height: 61px
    position: absolute
    right: 0
    width: 61px
</style>



// WEBPACK FOOTER //
// src/components/contents/ContentForm.vue